import React, { useState, useEffect } from 'react';
import { Calendar, Search, Filter, ThumbsUp, ThumbsDown, Clock, XCircle, ChevronDown, ChevronUp, ExternalLink, BarChart3, TrendingUp, Award, AlertCircle, Circle } from 'lucide-react';
import { buildApiUrl } from '../config';
import { calculatePartisanScore } from '../scripts/bills/partisanScoring';
import { getPartisanDataFromScore } from '../scripts/bills/partisanScoreUtils';
import { getPolicyTagIcon, getPolicyTagColor, getAllPolicyTagsSync } from '../scripts/bills/policyTagUtils';

const VotingRecords = ({ politician, isDarkMode, getPartyColor, getPartyButtonClass, getPartyGradientBg, onNavigateToBill, selectedTag }) => {
  const [votingRecord, setVotingRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [voteFilter, setVoteFilter] = useState('all'); // all, yea, nay, nv, absent
  const [expandedBill, setExpandedBill] = useState(null);
  const [sortBy, setSortBy] = useState('date'); // date, bill, vote
  const [billsData, setBillsData] = useState({}); // Cache for real bill data
  const [loadingBillData, setLoadingBillData] = useState(false); // Track bill data loading
  const [selectedPolicyTags, setSelectedPolicyTags] = useState([]); // Selected policy tags for filtering
  const [partisanFilter, setPartisanFilter] = useState('all'); // Filter by partisan labels

  // Fetch voting record from new database structure
  useEffect(() => {
    if (politician) {
      fetchVotingRecord();
    }
  }, [politician]);

  // Auto-collapse expanded vote when policy tag filter changes
  useEffect(() => {
    setExpandedBill(null);
  }, [selectedPolicyTags]);

  // Set initial policy tag filter based on selectedTag prop
  useEffect(() => {
    if (selectedTag && selectedTag !== 'all') {
      setSelectedPolicyTags([selectedTag]);
    } else {
      setSelectedPolicyTags([]);
    }
  }, [selectedTag]);

  const fetchVotingRecord = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🔍 Searching for voting records for: "${politician.name}"`);

      const politicianId = politician?.id;
      if (!politicianId) {
        throw new Error('Missing politician id for voting record lookup');
      }

      const response = await fetch(buildApiUrl(`/api/politician-voting-record/id/${encodeURIComponent(politicianId)}`));

      if (!response.ok) {
        console.log(`❌ API returned ${response.status} for politician "${politician.name}"`);
        throw new Error('Failed to fetch voting records');
      }

      const data = await response.json();
      const normalizedVotes = (data.votes || []).map(vote => {
        const voteValue = vote.vote_value || '';
        let voteText = voteValue;
        if (voteValue === 'Yes') voteText = 'Yea';
        else if (voteValue === 'No') voteText = 'Nay';
        else if (voteValue === 'NV') voteText = 'NV';
        else if (voteValue === 'Absent') voteText = 'Absent';

        return {
          ...vote,
          bill_number: vote.bills?.bill_number || '',
          bill_title: vote.bills?.title || '',
          vote_text: voteText,
          date: vote.vote_date || null
        };
      });

      const normalizedRecord = {
        ...data,
        votes: normalizedVotes,
        chronological_votes: normalizedVotes
      };

      console.log(`✅ Successfully loaded voting records for: "${normalizedRecord.name}" with ${normalizedRecord.voting_stats?.total_votes || 0} votes`);
      setVotingRecord(normalizedRecord);

      // Fetch real bill data BEFORE showing the UI
      console.log(`🔄 Fetching bill data before showing UI...`);
      await fetchBillsData(normalizedRecord.chronological_votes || []);

      setLoading(false); // Show voting records after bill data is loaded
    } catch (err) {
      console.error('Error fetching voting record:', err);
      setError('Failed to load voting records');
      setLoading(false);
    }
  };

  // Fetch real bill data from the bills API (ultra-fast parallel loading)
  const fetchBillsData = async (votes) => {
    const billNumbers = [...new Set(votes.map(vote => vote.bill_number))]; // Get unique bill numbers

    setLoadingBillData(true);
    console.log(`🚀 Starting ultra-fast parallel bill data fetch for ${billNumbers.length} unique bills`);
    console.log(`📋 Sample bill numbers:`, billNumbers.slice(0, 5));

    try {
      // Strategy 1: Parallel batch API calls with chunking
      const billDataCache = {};
      const chunkSize = 50; // Optimal chunk size for parallel processing
      const chunks = [];

      // Split bills into chunks
      for (let i = 0; i < billNumbers.length; i += chunkSize) {
        chunks.push(billNumbers.slice(i, i + chunkSize));
      }

      console.log(`📦 Processing ${chunks.length} chunks of ${chunkSize} bills each`);

      // Process all chunks in parallel
      const chunkPromises = chunks.map(async (chunk, chunkIndex) => {
        try {
          const numbersParam = chunk.join(',');
          const response = await fetch(buildApiUrl(`/api/bills/batch?numbers=${encodeURIComponent(numbersParam)}`));

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.bills) {
              console.log(`✅ Chunk ${chunkIndex + 1}: loaded ${data.count} bills`);
              return data.bills;
            }
          }
          throw new Error(`Batch API returned ${response.status}`);
        } catch (error) {
          console.warn(`❌ Chunk ${chunkIndex + 1} failed, using individual fallback:`, error);

          // Individual fallback for this chunk
          const individualPromises = chunk.map(async (billNumber) => {
            try {
              const response = await fetch(buildApiUrl(`/api/bills?search=${encodeURIComponent(billNumber)}&limit=1`));
              if (response.ok) {
                const data = await response.json();
                if (data.bills && data.bills.length > 0) {
                  return { [billNumber]: data.bills[0] };
                }
              }
            } catch (err) {
              console.warn(`Failed to fetch ${billNumber}:`, err);
            }
            return {};
          });

          const individualResults = await Promise.all(individualPromises);
          return Object.assign({}, ...individualResults);
        }
      });

      // Wait for all chunks to complete
      const chunkResults = await Promise.all(chunkPromises);

      // Merge all results
      chunkResults.forEach(result => {
        Object.assign(billDataCache, result);
      });

      // Update UI with all results at once
      setBillsData(billDataCache);
      console.log(`✅ Ultra-fast loading complete: ${Object.keys(billDataCache).length} of ${billNumbers.length} bills loaded`);
      console.log(`📊 Sample loaded bills:`, Object.keys(billDataCache).slice(0, 5));
      console.log(`📊 Sample bill data for HB105:`, billDataCache['HB105']?.title);

    } catch (error) {
      console.error('❌ All parallel methods failed, using aggressive fallback:', error);

      // Aggressive fallback: massive parallel individual requests
      const billDataCache = {};
      const batchSize = 20; // Large batches for maximum parallelism

      for (let i = 0; i < billNumbers.length; i += batchSize) {
        const batch = billNumbers.slice(i, i + batchSize);

        // Process batch in parallel
        const promises = batch.map(async (billNumber) => {
          try {
            const response = await fetch(buildApiUrl(`/api/bills?search=${encodeURIComponent(billNumber)}&limit=1`));
            if (response.ok) {
              const data = await response.json();
              if (data.bills && data.bills.length > 0) {
                return { billNumber, bill: data.bills[0] };
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch bill data for ${billNumber}:`, err);
          }
          return null;
        });

        const results = await Promise.all(promises);

        // Update cache with successful results
        results.forEach(result => {
          if (result) {
            billDataCache[result.billNumber] = result.bill;
          }
        });

        // Update UI progressively
        setBillsData(prev => ({ ...prev, ...billDataCache }));

        console.log(`📊 Fallback: Fetched batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(billNumbers.length / batchSize)} - ${Object.keys(billDataCache).length} bills loaded`);

        // Minimal delay for fallback
        if (i + batchSize < billNumbers.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    }

    setLoadingBillData(false);
    console.log(`✅ Completed bill data fetch`);
  };

  // Create a function to get enhanced bill title using real bill data
  const getEnhancedBillTitle = (vote) => {
    const realBill = billsData[vote.bill_number];

    // Debug logging to see what's happening
    console.log(`🔍 Getting title for ${vote.bill_number}:`, {
      hasRealBill: !!realBill,
      realBillTitle: realBill?.title,
      fallbackTitle: vote.bill_title,
      billsDataKeys: Object.keys(billsData).slice(0, 5) // Show first 5 keys
    });

    if (realBill) {
      // Use the cleaner title if available
      const cleanTitle = realBill.display_title || realBill.title;
      console.log(`✅ Using clean title: "${cleanTitle}"`);
      return cleanTitle;
    }

    // Fallback to the voting record title (remove semicolons and clean up)
    const fallbackTitle = (vote.bill_title || `Bill ${vote.bill_number}`)
      .replace(/;/g, '') // Remove semicolons
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    console.log(`⚠️ Using fallback title: "${fallbackTitle}"`);
    return fallbackTitle;
  };


  // Get all available vote types for dropdown (static from voting record)
  const getAvailableVoteTypes = () => {
    return [
      { value: 'all', label: 'All Votes' },
      { value: 'yea', label: 'Yea Votes' },
      { value: 'nay', label: 'Nay Votes' },
      { value: 'nv', label: 'Not Voting' },
      { value: 'absent', label: 'Absent' }
    ];
  };

  // Get all available partisan labels for dropdown (static list)
  const getStaticPartisanLabels = () => {
    return [
      'All Partisan Labels',
      'Strongly Democratic',
      'Moderately Democratic',
      'Leaning Democratic',
      'Bipartisan',
      'Leaning Republican',
      'Moderately Republican',
      'Strongly Republican'
    ];
  };

  // Get comprehensive policy tags list from utility file (alphabetized for easy scanning)
  const getStaticPolicyTags = () => {
    const allTags = getAllPolicyTagsSync(); // Use sync version for immediate rendering
    const sorted = [...(allTags || [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return ['All Policy Tags', ...sorted];
  };



  // Create a function to get real partisan score badge
  const getPartisanScoreBadge = (vote) => {
    const realBill = billsData[vote.bill_number];

    if (realBill) {
      // Use stored partisan score from database if available, otherwise calculate it
      const partisanData = (realBill.partisan_score !== undefined && realBill.partisan_score !== null)
        ? getPartisanDataFromScore(realBill.partisan_score)
        : calculatePartisanScore(realBill);

      if (partisanData && partisanData.score !== undefined) {
        const score = Math.round(partisanData.score);
        const label = partisanData.label; // Use the actual label from partisan scoring
        const direction = partisanData.direction; // Use the direction (Democratic/Republican/Neutral)

        // Debug logging to see what scores we're getting
        console.log(`📊 Bill ${vote.bill_number}: Score=${partisanData.signedScore}, Label="${label}", Direction="${direction}"`)

        // Determine color based on direction and strength
        let colorClass;
        if (direction === 'Democratic') {
          colorClass = isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
        } else if (direction === 'Republican') {
          colorClass = isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700';
        } else {
          // Bipartisan/Neutral
          colorClass = isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800';
        }

        return {
          score,
          label,
          direction,
          colorClass
        };
      }
    }

    // Fallback when no real data is available
    return {
      score: 0,
      label: 'Unknown',
      direction: 'Unknown',
      colorClass: isDarkMode ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-100 text-gray-600'
    };
  };

  // Filter and sort votes
  const votesList = (votingRecord?.chronological_votes || votingRecord?.votes || []);
  const filteredVotes = votesList.filter(vote => {
    const matchesSearch = !searchTerm ||
      vote.bill_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vote.bill_title.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = voteFilter === 'all' || vote.vote_text.toLowerCase() === voteFilter.toLowerCase();

    // Policy tag filtering
    const matchesPolicyTags = selectedPolicyTags.length === 0 || (() => {
      const realBill = billsData[vote.bill_number];
      if (!realBill || !realBill.policy_tags) return false;
      return selectedPolicyTags.some(tag => realBill.policy_tags.includes(tag));
    })();

    // Partisan label filtering
    const matchesPartisanFilter = partisanFilter === 'all' || (() => {
      const realBill = billsData[vote.bill_number];
      if (!realBill) return false;
      // Use stored partisan score from database if available, otherwise calculate it
      const partisanData = (realBill.partisan_score !== undefined && realBill.partisan_score !== null)
        ? getPartisanDataFromScore(realBill.partisan_score)
        : calculatePartisanScore(realBill);
      return partisanData && partisanData.label === partisanFilter;
    })();

    return matchesSearch && matchesFilter && matchesPolicyTags && matchesPartisanFilter;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'bill':
        return a.bill_number.localeCompare(b.bill_number);
      case 'vote':
        return a.vote_text.localeCompare(b.vote_text);
      case 'date':
      default:
        return new Date(b.date) - new Date(a.date);
    }
  }) || [];

  // Safe stats and date range fallbacks
  const stats = votingRecord?.voting_stats || {};
  const totalVotes = (stats.total_votes != null ? stats.total_votes : votesList.length) || 0;
  const yeaVotes = stats.yea_votes ?? 0;
  const nayVotes = stats.nay_votes ?? 0;
  const nvVotes = stats.nv_votes ?? 0;
  const absentVotes = stats.absent_votes ?? 0;
  const attendanceRate = stats.attendance_rate ?? 0;
  const yeaPct = stats.yea_percentage ?? (totalVotes ? Math.round((yeaVotes / totalVotes) * 100) : 0);
  const nayPct = stats.nay_percentage ?? (totalVotes ? Math.round((nayVotes / totalVotes) * 100) : 0);
  const dateRange = votingRecord?.date_range || {};
  const firstVote = dateRange.first_vote || null;
  const lastVote = dateRange.last_vote || null;

  const getVoteIcon = (voteText) => {
    switch (voteText) {
      case 'Yea':
        return <ThumbsUp className="w-4 h-4 text-green-500" />;
      case 'Nay':
        return <ThumbsDown className="w-4 h-4 text-red-500" />;
      case 'NV':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'Absent':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getVoteColor = (voteText) => {
    switch (voteText) {
      case 'Yea':
        return isDarkMode ? 'text-green-400 bg-green-900/20' : 'text-green-700 bg-green-100';
      case 'Nay':
        return isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-700 bg-red-100';
      case 'NV':
        return isDarkMode ? 'text-yellow-400 bg-yellow-900/20' : 'text-yellow-700 bg-yellow-100';
      case 'Absent':
        return isDarkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-600 bg-gray-100';
      default:
        return isDarkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading voting records and bill details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No voting records available</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            This politician doesn't have voting records in our database yet.
          </p>
        </div>
      </div>
    );
  }

  if (!votingRecord) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No voting records found</h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            No voting records available for this politician.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">

      {/* Voting Statistics Overview - Compact */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-2.5 md:p-4 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-1.5 md:mb-2">
          <h3 className={`text-[13px] md:text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5" />
            Voting Statistics
          </h3>
          <div className="flex items-center space-x-1 text-[11px] md:text-xs">
            <Award className={`w-3 h-3 md:w-3 md:h-3 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <span className={`font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
              {attendanceRate}% Attendance
            </span>
          </div>
        </div>

        {/* Stats layout: Mobile two-column grid, Desktop horizontal */}
        {/* Mobile */}
        <div className="md:hidden">
          <div className="grid grid-cols-2 gap-x-8">
            {/* Left column: Yea (+%) and Not Voted */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-green-400' : 'bg-green-600'}`}></div>
                <span className={`text-xs font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {yeaVotes} Yea
                </span>
                <span className={`text-[10px] ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  ({yeaPct}%)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-yellow-400' : 'bg-yellow-600'}`}></div>
                <span className={`text-xs font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {nvVotes} Not Voted
                </span>
              </div>
            </div>
            {/* Right column: Nay (+%) and Absent */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-red-400' : 'bg-red-600'}`}></div>
                <span className={`text-xs font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  {nayVotes} Nay
                </span>
                <span className={`text-[10px] ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  ({nayPct}%)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-gray-400' : 'bg-gray-600'}`}></div>
                <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {absentVotes} Absent
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2 flex justify-between items-baseline text-[11px]">
            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total: {totalVotes}
            </span>
            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {(firstVote || '—')} to {(lastVote || '—')}
            </span>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex space-x-8">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-green-400' : 'bg-green-600'}`}></div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                {yeaVotes} Yea
              </span>
              <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                ({yeaPct}%)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-red-400' : 'bg-red-600'}`}></div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                {nayVotes} Nay
              </span>
              <span className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                ({nayPct}%)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-yellow-400' : 'bg-yellow-600'}`}></div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                {nvVotes} Not Voted
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-gray-400' : 'bg-gray-600'}`}></div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {absentVotes} Absent
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-s ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total: {totalVotes}
            </div>
            <div className={`text-[12px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {(firstVote || '—')} to {(lastVote || '—')}
            </div>
          </div>
        </div>
      </div>



      {/* Search and Filter Controls */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 md:p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {/* Search Bar - Top Half */}
        <div className="mb-2 md:mb-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-4 h-4`} />
            <input
              type="text"
              placeholder="Search bills by number or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-9 pr-3 py-2 text-sm md:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
            />
          </div>
        </div>

        {/* Dropdown Controls - Bottom Half */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
          <select
            value={voteFilter}
            onChange={(e) => setVoteFilter(e.target.value)}
            className={`px-2.5 py-2 text-sm md:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            {getAvailableVoteTypes().map(voteType => (
              <option key={voteType.value} value={voteType.value}>
                {voteType.label}
              </option>
            ))}
          </select>

          <select
            value={selectedPolicyTags.length > 0 ? selectedPolicyTags[0] : 'all'}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                setSelectedPolicyTags([]);
              } else {
                setSelectedPolicyTags([value]);
              }
            }}
            className={`px-2.5 py-2 text-sm md:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            {getStaticPolicyTags().map(tag => (
              <option key={tag} value={tag === 'All Policy Tags' ? 'all' : tag}>
                {tag}
              </option>
            ))}
          </select>

          <select
            value={partisanFilter}
            onChange={(e) => setPartisanFilter(e.target.value)}
            className={`px-2.5 py-2 text-sm md:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            {getStaticPartisanLabels().map(label => (
              <option key={label} value={label === 'All Partisan Labels' ? 'all' : label}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-2.5 py-2 text-sm md:text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full ${isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            <option value="date">Sort by Date</option>
            <option value="bill">Sort by Bill</option>
            <option value="vote">Sort by Vote</option>
          </select>
        </div>

        {/* Vote Results Count */}
        <div className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-3 md:mt-4 pt-2.5 md:pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <span>Showing {filteredVotes.length} of {totalVotes} votes</span>
          {loadingBillData && (
            <div className="flex items-center space-x-1 ml-2">
              <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-xs">Loading bill details...</span>
            </div>
          )}
        </div>
      </div>

      {/* Voting Records List */}
      <div className="space-y-2.5 md:space-y-3">
        {filteredVotes.length === 0 ? (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} text-center`}>
            <Search className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No votes found matching your search criteria.
            </p>
          </div>
        ) : (
          filteredVotes.map((vote, index) => (
            <div
              key={index}
              onClick={() => setExpandedBill(expandedBill === index ? null : index)}
              className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden transition-all duration-200 hover:shadow-lg relative cursor-pointer`}
            >
              <div className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start md:items-center md:space-x-4 flex-1">
                    {/* Mobile: vote icon and badge pinned top-right */}
                    <div className="md:hidden absolute bottom-3 right-3 flex items-center space-x-2">
                      {getVoteIcon(vote.vote_text)}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getVoteColor(vote.vote_text)}`}>
                        {vote.vote_text}
                      </span>
                    </div>
                    {/* Desktop: inline vote group */}
                    <div className="hidden md:flex items-center space-x-2">
                      {getVoteIcon(vote.vote_text)}
                      <span className={`px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium ${getVoteColor(vote.vote_text)}`}>
                        {vote.vote_text}
                      </span>
                    </div>

                    <div className="flex-1 md:pl-0 pl-1">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3">
                        <span className={`font-bold text-sm md:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {vote.bill_number}
                        </span>
                        <span className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {vote.date}
                        </span>
                        {(() => {
                          const partisanData = getPartisanScoreBadge(vote);
                          return (
                            <span className={`text-[11px] md:text-xs px-2 py-0.5 md:py-1 rounded font-medium ${partisanData.colorClass}`}>
                              {partisanData.label}
                            </span>
                          );
                        })()}
                      </div>
                      <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mt-1 line-clamp-2`}>
                        {getEnhancedBillTitle(vote)}
                      </p>

                      {/* Policy Tags */}
                      {(() => {
                        const realBill = billsData[vote.bill_number];
                        if (realBill && realBill.policy_tags && realBill.policy_tags.length > 0) {
                          return (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {realBill.policy_tags.map((tag, tagIdx) => {
                                const IconComponent = getPolicyTagIcon(tag);
                                const tagColor = getPolicyTagColor(tag, isDarkMode);
                                return (
                                  <span key={tagIdx} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${tagColor} shadow-sm`}>
                                    <IconComponent className="w-3 h-3 mr-1" />
                                    {tag}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 self-start md:self-auto">
                    {onNavigateToBill && (
                      <>
                        {/* Icon-only on mobile, labeled on desktop */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToBill(vote.bill_number);
                          }}
                          className={`p-2 rounded-lg text-xs font-medium transition-all md:hidden ${isDarkMode
                              ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          aria-label="View Bill"
                          title="View Bill"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToBill(vote.bill_number);
                          }}
                          className={`hidden md:flex px-3 py-1 rounded-lg text-xs font-medium transition-all ${isDarkMode
                              ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            } items-center space-x-1`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>View Bill</span>
                        </button>
                      </>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedBill(expandedBill === index ? null : index);
                      }}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      {expandedBill === index ? (
                        <ChevronUp className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      ) : (
                        <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                      )}
                    </button>
                  </div>
                </div>

                {expandedBill === index && (
                  <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="text-sm">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Summary:
                      </span>
                      <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {(() => {
                          const realBill = billsData[vote.bill_number];
                          return realBill?.ai_summary || 'No summary found';
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VotingRecords;
